/* eslint-disable object-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import Media from 'react-media';
import { useLocation } from 'react-router-dom';
import { Layout } from '../../contexts/LayoutContext';
import CreateAccountForm from './Form';
import PiperPresentation from './components/PiperPresentation';
import mediaQueries from '../../utils/mediaQueries';
import { container, lowSpreadShadow } from '../../styled/mixins';
import factoryPromoCodeComponent from './components/factoryPromoCode';
import GlobalTheme from '../../styled/GlobalTheme';
import FacebookButton from '../auth/components/FacebookButton';
import { colors } from '../../styled/theme';
import roles from '../../utils/constants/roles';
import PlansSelector from './components/PlansSelector';
import PromoCodeContext from '../../contexts/PromoCodeContext';
import { WebViewContext } from '../../contexts/WebViewContext';
import TutorialModal from './components/tutorialModal/TutorialModal';
import AppleButton from '../auth/components/AppleButton';
import StrokeBox from '../Login/components/StrokeBox';

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 740px;
  ${GlobalTheme.breakpoints.up('md')} {
    margin: 0 auto;
  }
  ${GlobalTheme.breakpoints.up('lg')} {
    margin: 0 auto;
  }
`;

const LegalAccept = styled.div`
  margin-top: 50px;
  font-size: 16px;
  color: ${(props) => props.theme.colors.text02};
  text-align: center;
`;

const LegalLink = styled.a`
  color: ${(props) => props.theme.colors.lightBlue};
  text-decoration: underline;
`;

const Container = styled.div`
  ${container};
  padding-top: 28px;
  padding-bottom: 16px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-gap: ${(props) => props.theme.space * 4}px;
  grid-template-columns: 245px 1fr;
  ${GlobalTheme.breakpoints.down('lg')}{
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: ${({ margin }) => (margin || '0 0 32px')};
  width: 100%;
  opacity: ${({ opacity }) => (opacity || '1')};
`;

const TitleHeader = styled.div`
  color: black;
  font-size: 24px;
  font-weight: 600;
  text-align: left;
  height: 70px;
  width: 300px;
  line-height: 30px;
  display: none;
  ${GlobalTheme.breakpoints.down('sm')} {
    display: inline;
  }
`;

const Selectors = styled.div`
  display: flex;
  align-items: stretch;
  margin-top: 16px;
  width: 100%;
  justify-content: center;

  &:nth-child(1) {
    color: red;
  }
`;

const ButtonContainerSN = styled.div`
  text-align: center;
  margin: 0 20%;
  width: 60%;
  &:last-child {
    margin-top: 25px;
  }
  ${GlobalTheme.breakpoints.up('sm')} {
    width: 44%;
  }
`;

const RecurrenceSelector = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.grayE9};
  margin-right: 1px;
  padding: 2px;
  width: 140px;
  flex: 0 0 140px;
  color: ${colors.gray90};
  font-size: 16px;
  ${GlobalTheme.breakpoints.down('sm')} {
    font-size: 12px;
    width: 80px;
    flex: 0 0 80px;
  }
  ${({ isActive }) => isActive && css`
    color: white;
    border-color: ${colors.green};
    background: ${colors.green};
    ${lowSpreadShadow}
  `}
  span {
    font-size: 10px;
    ${GlobalTheme.breakpoints.down('sm')} {
      font-size: 9px;
    }
  }

  &:nth-child(1) {
    flex: 0 0 450px;
    color: black;
    font-size: 25px;
    font-weight: 600;
    border: 0;

    ${GlobalTheme.breakpoints.down('sm')} {
      display: none;
    }
  }
`;

const CreateAccount = () => {
  const intl = useIntl();
  const location = useLocation();
  const { PromoCode, handlePromoCodeChange } = useContext(PromoCodeContext);
  const { isAppIOS } = useContext(WebViewContext);
  const [isMonthly, setIsMonthly] = useState(true);
  const [planSelected, setPlanSelected] = useState('P');

  useEffect(() => {
    setPlanSelected((location.state && location.state.plan) || 'P');
  }, []);

  return (
    <Media queries={mediaQueries}>
      {(matches) => (
        <>
          <Layout
            title={intl.formatMessage({ id: 'onboarding.createAccount.title', defaultMessage: 'Create your account' })}
            description={intl.formatMessage({ id: 'onboarding.createAccount.description', defaultMessage: 'Start your 30-day free trial.' })}
            accessOnTrialEnds
          >
            <Container>
              <MainGrid>
                {matches.smUp && (
                  <PiperPresentation position="right" />
                )}
                <ContentSection>
                  <SectionHeader>
                    <TitleHeader>
                      {intl.formatMessage({ id: 'onboarding.createAccount.title.app', defaultMessage: 'Create account with the Pro plan' })}
                    </TitleHeader>
                    <Selectors>
                      <RecurrenceSelector>
                        {intl.formatMessage({ id: 'onboarding.createAccount.title.app', defaultMessage: 'Create account with the Pro plan' })}
                      </RecurrenceSelector>
                      <RecurrenceSelector
                        isActive={isMonthly}
                        onClick={() => setIsMonthly(true)}
                      >
                        <FormattedMessage
                          id="k.monthly"
                          defaultMessage="Monthly"
                        />
                      </RecurrenceSelector>
                      <RecurrenceSelector
                        isActive={!isMonthly}
                        onClick={() => setIsMonthly(false)}
                      >
                        <FormattedMessage
                          id="k.yearly"
                          defaultMessage="Yearly"
                        />
                        <span>
                          <FormattedMessage
                            id="onboarding.createAccount.selectPlan.save"
                            defaultMessage="Save 2 months"
                          />
                        </span>
                      </RecurrenceSelector>
                    </Selectors>
                  </SectionHeader>
                  <PlansSelector
                    planSelected={planSelected}
                    setPlanSelected={setPlanSelected}
                    isMonthly={isMonthly}
                    hasSelector
                  />

                  {!isAppIOS && (factoryPromoCodeComponent(PromoCode, handlePromoCodeChange))}

                  <CreateAccountForm
                    wantsFree={planSelected === roles.PRO}
                    planSelected={planSelected}
                  />

                  <ButtonContainerSN>
                    <StrokeBox />
                  </ButtonContainerSN>
                  <ButtonContainerSN>
                    <FacebookButton
                      PromoCode={PromoCode}
                      wantsFree={planSelected === roles.BASIC}
                      disabled={!planSelected}
                      buttonType="signupapp"
                    />
                  </ButtonContainerSN>
                  <ButtonContainerSN>
                    <AppleButton wantsFree={false} buttonType="signup" />
                  </ButtonContainerSN>

                </ContentSection>
              </MainGrid>
              <LegalAccept>
                <FormattedMessage
                  id="onboarding.creteAccount.acceptAllTerms"
                  defaultMessage="By registering, you are accepting: <terms>Terms of Service</terms>, <cookies>Cookies Policy</cookies>, <privacy>Privacy Policy</privacy>, <caPrivacy>CA Privacy Rights</caPrivacy> and <doNotSell>Do Not Sell My Information</doNotSell> of Social Piper, and you will be automatically registered to receive emails about new Social Piper products, best practices or special offers, until you opt out."
                  values={{
                    terms: (...chunks) => <LegalLink target="_blank" href="https://legal.socialpiper.com/social-piper-terms-of-service-2020-07-31/">{chunks}</LegalLink>,
                    cookies: (...chunks) => <LegalLink target="_blank" href="https://legal.socialpiper.com/social-piper-cookie-policy-2020-08-03/">{chunks}</LegalLink>,
                    privacy: (...chunks) => <LegalLink target="_blank" href="https://legal.socialpiper.com/social-piper-privacy-policy-2020-08-03/">{chunks}</LegalLink>,
                    caPrivacy: (...chunks) => <LegalLink target="_blank" href="https://legal.socialpiper.com/social-piper-ca-privacy-policy-2020-08-03/">{chunks}</LegalLink>,
                    doNotSell: (...chunks) => <LegalLink target="_blank" href="https://legal.socialpiper.com/social-piper-do-not-sell-my-personal-information-2020-08-03/">{chunks}</LegalLink>,
                  }}
                />
              </LegalAccept>
            </Container>
          </Layout>
          <TutorialModal />
        </>
      )}
    </Media>
  );
};

export default CreateAccount;
